import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, AlertTriangle, UserCheck, Pencil, Package } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ImportClient } from '@/hooks/useClientCSVImport';

type EditableField = 'firstName' | 'lastName' | 'email' | 'phone' | 'emergencyContact' | 'subscriptionType' | 'subscriptionDate' | 'paymentStatus';

interface EditablePreviewTableProps {
  clients: ImportClient[];
  onUpdateClient: (rowIndex: number, field: EditableField, value: string) => void;
}

export function EditablePreviewTable({ clients, onUpdateClient }: EditablePreviewTableProps) {
  const { t } = useTranslation(['clients']);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; field: EditableField } | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = useCallback((rowIndex: number, field: EditableField, currentValue: string) => {
    setEditingCell({ rowIndex, field });
    setEditValue(currentValue || '');
  }, []);

  const confirmEdit = useCallback(() => {
    if (editingCell) {
      onUpdateClient(editingCell.rowIndex, editingCell.field, editValue);
      setEditingCell(null);
      setEditValue('');
    }
  }, [editingCell, editValue, onUpdateClient]);

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      confirmEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }, [confirmEdit, cancelEdit]);

  const renderEditableCell = (
    client: ImportClient, 
    field: EditableField, 
    value: string,
    hasWarning?: boolean
  ) => {
    const isEditing = editingCell?.rowIndex === client.rowIndex && editingCell?.field === field;
    
    // Determine if this field has an error
    const hasError = field === 'email' 
      ? client.errors.some(e => e.toLowerCase().includes('email'))
      : (field === 'firstName' || field === 'lastName')
        ? client.errors.some(e => e.toLowerCase().includes('nom') || e.toLowerCase().includes('name'))
        : false;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-7 text-sm min-w-[80px]"
            autoFocus
          />
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={confirmEdit}>
            <Check className="h-3 w-3 text-emerald-600" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={cancelEdit}>
            <X className="h-3 w-3 text-destructive" />
          </Button>
        </div>
      );
    }

    return (
      <div 
        className={cn(
          "flex items-center gap-1 group cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 py-0.5",
          hasError && "text-destructive",
          hasWarning && "text-yellow-600"
        )}
        onClick={() => startEdit(client.rowIndex, field, value)}
      >
        <span className="truncate">{value || '-'}</span>
        <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity shrink-0" />
      </div>
    );
  };

  // Check if any client has subscription data
  const hasSubscriptionData = clients.some(c => c.subscriptionType || c.subscriptionDate || c.paymentStatus);

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground flex items-center gap-1">
        <Pencil className="h-4 w-4" />
        {t('import.preview.editHint')}
      </p>
      <ScrollArea className="h-[400px] rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-12">{t('import.preview.status')}</TableHead>
              <TableHead>{t('firstName')}</TableHead>
              <TableHead>{t('lastName')}</TableHead>
              <TableHead>{t('email')}</TableHead>
              <TableHead>{t('phone')}</TableHead>
              <TableHead>{t('emergencyContact')}</TableHead>
              {hasSubscriptionData && (
                <>
                  <TableHead>{t('import.preview.pack')}</TableHead>
                  <TableHead>{t('import.preview.date')}</TableHead>
                  <TableHead>{t('import.preview.payment')}</TableHead>
                </>
              )}
              <TableHead>{t('import.preview.issues')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client) => (
              <TableRow 
                key={client.rowIndex}
                className={cn(!client.isValid && 'bg-destructive/5')}
              >
                <TableCell>
                  <StatusIcon client={client} />
                </TableCell>
                <TableCell className="font-medium">
                  {renderEditableCell(client, 'firstName', client.firstName)}
                </TableCell>
                <TableCell>
                  {renderEditableCell(client, 'lastName', client.lastName)}
                </TableCell>
                <TableCell className="text-sm">
                  {renderEditableCell(client, 'email', client.email)}
                </TableCell>
                <TableCell className="text-sm">
                  {renderEditableCell(client, 'phone', client.phone)}
                </TableCell>
                <TableCell className="text-sm">
                  {renderEditableCell(client, 'emergencyContact', client.emergencyContact)}
                </TableCell>
                {hasSubscriptionData && (
                  <>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        {renderEditableCell(client, 'subscriptionType', client.subscriptionType || '', !!client.subscriptionError)}
                        {client.subscriptionTypeId && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Package className="h-3 w-3 text-emerald-600" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <span className="text-xs">{t('import.preview.packMatched')}</span>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {client.subscriptionError && !client.subscriptionTypeId && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className="h-3 w-3 text-yellow-500" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <span className="text-xs">{client.subscriptionError}</span>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {renderEditableCell(client, 'subscriptionDate', client.subscriptionDate || '')}
                    </TableCell>
                    <TableCell className="text-sm">
                      <PaymentStatusBadge status={client.paymentStatus} />
                    </TableCell>
                  </>
                )}
                <TableCell>
                  {client.errors.length > 0 ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="text-destructive border-destructive/50 cursor-help">
                            {client.errors.length} {client.errors.length === 1 ? t('import.preview.error') : t('import.preview.errors')}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs">
                          <ul className="text-xs space-y-1">
                            {client.errors.map((error, i) => (
                              <li key={i}>• {error}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                      {t('import.preview.ready')}
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

function StatusIcon({ client }: { client: ImportClient }) {
  const { t: tClient } = useTranslation(['clients']);
  if (client.isValid) {
    return <Check className="h-5 w-5 text-emerald-600" />;
  }
  
  if (client.existsInDatabase) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <UserCheck className="h-5 w-5 text-blue-500" />
          </TooltipTrigger>
          <TooltipContent>
            <span className="text-xs">{tClient('importPreview.existingClient', { ns: 'adminui3' })}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  if (client.isDuplicateInFile) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          </TooltipTrigger>
          <TooltipContent>
            <span className="text-xs">{tClient('importPreview.duplicateInFile', { ns: 'adminui3' })}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return <X className="h-5 w-5 text-destructive" />;
}

function PaymentStatusBadge({ status }: { status?: string }) {
  const { t } = useTranslation(['clients']);
  
  if (!status) return <span className="text-muted-foreground">-</span>;
  
  switch (status.toUpperCase()) {
    case 'P':
      return (
        <Badge variant="outline" className="text-emerald-600 border-emerald-200">
          {t('import.preview.paid')}
        </Badge>
      );
    case 'NP':
      return (
        <Badge variant="outline" className="text-yellow-600 border-yellow-200">
          {t('import.preview.notPaid')}
        </Badge>
      );
    case 'G':
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-200">
          {t('import.preview.gift')}
        </Badge>
      );
    default:
      return <span className="text-muted-foreground">{status}</span>;
  }
}
